import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { actions } from 'react-redux-form'
import Form from 'components/form-components/form/form'
import InterviewQuestions from 'interview/components/interview-questions'
import InterviewForm from 'interview/components/interview-form'
import { mapLanguageToComponentProps } from 'interview/i18n'
import styled, { css } from 'styled-components'
import { Hr } from 'components/styled/typography'
import { getCompanyAndInterviewInfoForAmplitude } from 'selectors'
import agreeTos from 'components/agree-tos'

const propTypes = {
  candidate: PropTypes.shape({
    email: PropTypes.string,
    uuid: PropTypes.string,
    agreeTOS: PropTypes.bool,
  }).isRequired,
  interview: PropTypes.shape({
    questions: PropTypes.array,
    formSetup: PropTypes.object,
    type: PropTypes.string,
    company: PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
    }),
    additionalFormFields: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        type: PropTypes.string,
        required: PropTypes.bool,
      })
    ).isRequired,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  isPublic: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  formName: PropTypes.string,
  disableEmail: PropTypes.bool,
  preventEnterSubmit: PropTypes.func,
  updateAdditionalFieldsValidityState: PropTypes.func,
  agreeTosComponent: PropTypes.node,
  model: PropTypes.string,
  showRequiredWarning: PropTypes.bool,
}

const defaultProps = {
  isPublic: false,
  formName: 'interviewForm',
  model: 'candidate',
  disableEmail: false,
  preventEnterSubmit: () => {},
  children: undefined,
  updateAdditionalFieldsValidityState: undefined,
  agreeTosComponent: null,
  showRequiredWarning: false,
}

const mapStateToProps = (state, ownProps) => ({
  candidate:
    ownProps.model === 'previewCandidate'
      ? state.previewCandidate
      : state.candidate,
  interview:
    ownProps.model === 'previewCandidate'
      ? state.interview
      : state.interview.jobOpening,
})

const DisabledStyles = css`
  opacity: 0.5;
  pointer-events: none;
`

const DisabledFormWrapper = styled.div`
  ${props => props.$disabled && DisabledStyles};
`

export class CandidateForm extends Component {
  constructor(props) {
    super(props)

    this.state = {
      invalidFields: [],
    }

    this.onSubmit = this.onSubmit.bind(this)
    this.submitAdditionalFieldsForm = this.submitAdditionalFieldsForm.bind(this)
  }

  componentDidMount() {
    const {
      interview: { additionalFormFields = [] },
      updateAdditionalFieldsValidityState,
    } = this.props
    const { invalidFields } = this.state

    const visibleAdditionalFields = additionalFormFields.filter(
      field => field.fieldSetup.visible
    )
    if (
      updateAdditionalFieldsValidityState &&
      (visibleAdditionalFields.length === 0 || invalidFields.length === 0)
    ) {
      updateAdditionalFieldsValidityState(true)
    }
  }

  componentWillUnmount() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  }

  onSubmit() {
    const {
      onSubmit,
      dispatch,
      interview: { additionalFormFields = [] },
      updateAdditionalFieldsValidityState,
      model,
    } = this.props
    const { invalidFields } = this.state
    const visibleFields = additionalFormFields.filter(
      field => field.fieldSetup.visible
    )

    if (visibleFields.length > 0) {
      document.additionalFieldsForm.submit()
    }

    if (invalidFields.length > 0) {
      dispatch(actions.setSubmitFailed(`${model}.additionalFieldResponses`))

      if (updateAdditionalFieldsValidityState) {
        updateAdditionalFieldsValidityState(false)
      }
    } else {
      onSubmit()
    }
  }

  checkAdditionalFieldsValidity(fields) {
    const { updateAdditionalFieldsValidityState } = this.props
    const { invalidFields } = this.state

    Object.keys(fields).forEach(key => {
      const isInInvalidFields = invalidFields.indexOf(key) > -1
      const validField = fields[key].valid
      if (!validField && !isInInvalidFields) {
        this.setState({
          invalidFields: [...invalidFields, key],
        })
        if (updateAdditionalFieldsValidityState) {
          updateAdditionalFieldsValidityState(
            [...invalidFields, key].length === 0
          )
        }
      } else if (validField && isInInvalidFields) {
        const filtered = invalidFields.filter(id => id !== key)
        this.setState({ invalidFields: filtered })
        if (updateAdditionalFieldsValidityState) {
          updateAdditionalFieldsValidityState(filtered.length === 0)
        }
      }
    })
  }

  submitAdditionalFieldsForm() {
    const {
      interview: { additionalFormFields = [] },
    } = this.props
    const visibleFields = additionalFormFields.filter(
      field => field.fieldSetup.visible
    )
    if (visibleFields.length > 0) {
      document.additionalFieldsForm.submit()
    }
  }

  render() {
    const {
      isPublic,
      candidate,
      interview,
      children,
      formName,
      onChange,
      disableEmail,
      preventEnterSubmit,
      agreeTosComponent,
      model,
      showRequiredWarning,
    } = this.props

    const { additionalFormFields = [], formSetup, questions, type } = interview

    const companyAndInterviewInfo =
      getCompanyAndInterviewInfoForAmplitude(interview)

    return (
      <div className="candidate-form">
        <Form
          model={model}
          onKeyPress={preventEnterSubmit}
          onSubmit={() => this.onSubmit()}
          onSubmitFailed={this.submitAdditionalFieldsForm}
          name={formName}
        >
          {!isPublic && agreeTosComponent}
          <DisabledFormWrapper $disabled={!isPublic && !candidate.agreeTOS}>
            <InterviewForm
              changeAction={onChange}
              publicInterview={isPublic}
              formSetup={formSetup}
              additionalFormFields={additionalFormFields}
              candidate={candidate}
              isFormValid={fields => this.checkAdditionalFieldsValidity(fields)}
              disableEmail={disableEmail}
              language={interview.language}
              model={model}
            />
            <Hr />
            {isPublic && agreeTosComponent}
            {!isPublic && type !== 'sequenced' && (
              <InterviewQuestions
                disableResponse={model === 'previewCandidate'}
                candidate={candidate}
                interviewCompanyData={companyAndInterviewInfo}
                questions={questions}
                showRequiredWarning={showRequiredWarning}
              />
            )}
          </DisabledFormWrapper>
          {children}
        </Form>
      </div>
    )
  }
}

CandidateForm.propTypes = propTypes
CandidateForm.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps(mapStateToProps))(
  CandidateForm
)
